import { createRef, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, createFilterOptions, Grid, IconButton, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography, useTheme,
} from "@mui/material";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { 
  IoCloseOutline, IoCreateOutline, IoCheckmark, IoEllipsisHorizontal,
  IoListOutline, IoPerson, IoPersonCircleOutline, IoPersonOutline, IoShareSocialSharp, IoTrashOutline
} from "react-icons/io5";
import { MdHowToVote } from "react-icons/md";
import { 
  CabAutocomplete, CabButton, CabControlLabel, CabIcon, CabInput, CabSwitch, CabTooltip,
  CabSpinner, CabAvatar,
  CabModal,
  CabTextInput
} from "@CabComponents";
import { emailRegex } from "../../../constants";
import colors from "../../../colors";
import {
  Leader, MeetingLeader, MeetingLeaderUpdate, ParticipantAutocompleteOption, PrivateExternalParticipant,
  PrivateExternalParticipantCreate, PrivateExternalParticipantUpdate, OrganizationLeader,
} from "../../../store";
import { useMountEffect } from "../../../utils/hooks";
import { getLeaderIconSrc } from "../../../utils/leaderUtils";
import { ParsedNameAndEmail, parseEmailAddresses } from "../../../utils/inputUtils";
import { getLocalTimeZoneNameOrNull } from "../../../utils/scheduleUtils";
import { useConfirmationModalWithUI } from "../../Common/ConfirmationModal";


export interface AttendeeListProps {
  attendees: PrivateExternalParticipant[];
  meetingLeaders?: MeetingLeader[];
  onAddAttendees: (participants: Omit<PrivateExternalParticipantCreate, 'meeting'>[]) => Promise<void>;
  onUpdateAttendee: (participant: PrivateExternalParticipantUpdate) => Promise<void>;
  onDeleteAttendee: (id: number) => void;
  participantAutocompleteOptions: ParticipantAutocompleteOption[];
  fetchAutocompleteOptions: (value: string) => void;
  isMdDown: boolean;
  hasAttendeeListOptions: boolean;
  onAddLeader?: (id: number) => void;
  onUpdateLeader?: (leader: MeetingLeaderUpdate, leaderId: number) => Promise<void>;
  onRemoveLeader?: (meetingLeaderId: number) => void;
  leaders?: Leader[];
  orgLeaders?: OrganizationLeader[];
  onHideAutocompleteContact?: (contact: ParticipantAutocompleteOption) => Promise<void>;
  onInviteOrgLeader?: (leaderId: number) => Promise<void>;
  showOrgLeaderShareLink?: boolean;
  readOnlyPollShareList?: boolean;
  onEditParticipants?: () => void;
  isAdditionalParticipantModal?: boolean;
  disableAdditionalParticipantsMessage?: string;
  meetingPreventConflicts?: boolean;
  meetingIsSaved: boolean;
  meetingIsPoll?: boolean;
}

type Participant = Pick<PrivateExternalParticipant | MeetingLeader,
'id'|'is_fetchable'|'prevent_conflicts'|'required'|'should_invite'|'view_calendar'>
& {
  email: string | null; iconSrc?: string | null; color?: string; name: string;
  isOrgLeader?: boolean; orgLeaderId?: number; orgLeaderManagerName?: string
  allow_poll_voting?: boolean;
};

type ParticipantUpdate = Omit<Participant, 'id'|'is_fetchable'> & { id: number };
type AddParticipantAutocompleteMeta = { group: string; contactId?: number };
type AddParticipantAutocompleteOption = {
  value: string;
  label: string;
  meta?: AddParticipantAutocompleteMeta;
  icon?: ReactElement;
};

interface FormInput {
  id?: number;
  email: string;
  name?: string;
  required: boolean;
  inviteToMeeting: boolean;
  showCalendar: boolean;
  preventConflicts: boolean;
}

interface AddEditAttendeeFormProps {
  defaultValues: FormInput, onSubmit: SubmitHandler<FormInput>, loading: boolean, isMdDown: boolean,
  submitButtonText: React.ReactNode, resetAfterSubmit?: boolean, onCancel?: () => void, 
  participantAutocompleteOptions: ParticipantAutocompleteOption[], fetchAutocompleteOptions: (value: string) => void;
  isAdditionalParticipantModal?: boolean; disabled?: boolean; allOtherAttendees: Participant[];
}

const getOrgUserName = (user?: OrganizationLeader['user_profile'] | OrganizationLeader['primary_assistant']) => {
  if (!user) return undefined;
  const name = `${user.first_name} ${user.last_name}`.trim();
  return name || user.email;
};

const AddEditAttendeeForm = ({
  defaultValues, onSubmit, loading, participantAutocompleteOptions, submitButtonText,
  resetAfterSubmit, onCancel, fetchAutocompleteOptions, isMdDown, isAdditionalParticipantModal, disabled,
  allOtherAttendees,
}: AddEditAttendeeFormProps) => {
  const { 
    control, handleSubmit: formSubmit, reset, setFocus, setValue, getValues, clearErrors
  } = useForm<FormInput>({ defaultValues });

  const [name, setName] = useState(getValues('name'));
  const [email, setEmail] = useState(getValues('email'));

  const attendeeRegex = /(?<name>[^()]*?) *\((?<email>[^()@]*@[^()@]*\.[^()@]*)\)/;

  useMountEffect(() => {
    setFocus('name');
  });

  useEffect(() => {
    setName(getValues('name'));
    setEmail(getValues('email'));
  }, [getValues]);

  const handleChangeInput = (param: 'name' | 'email', value: string) => {
    const match = attendeeRegex.exec(value);
    if (match && match.groups) {
      setName(match.groups.name);
      setEmail(match.groups.email);
      setValue('name', match.groups.name || ' '); // putting a single space which gets trimmed
      if (!isAdditionalParticipantModal) {
        setValue('email', match.groups.email);
      }
      clearErrors('email');
    } else {
      setValue(param, value);
    }
  };

  const handleSubmit = formSubmit((...args) => {
    onSubmit(...args);
    if (resetAfterSubmit) {
      reset();
      setFocus('email');
    }
  });

  const validateUniqueEmail = useCallback((eml: string) => {
    if (allOtherAttendees.map(a => (a.email || "").toLowerCase()).includes(eml.toLowerCase())) {
      return 'Emails must be unique';
    }
    return true;
  }, [allOtherAttendees]);

  return (
    <form onSubmit={handleSubmit}>
      <Grid container>
        <Grid size={12} container spacing={1}>
          <Grid size={5}>
            <Controller
              name="name"
              control={control}
              render={({ field: { ref, onChange, ...field } }) => isAdditionalParticipantModal ? (
                <CabInput
                  {...field}
                  onChange={onChange}
                  size="small"
                  disabled={loading || disabled}
                  sx={{ minWidth: isMdDown ? '150px' : '190px' }}
                  placeholder="Name (optional)"
                />
              ) : (
                <CabAutocomplete<string, never>
                  {...field}
                  inputRef={ref}
                  onChange={(v) => handleChangeInput('name', v ? (Array.isArray(v) ? v[0] : v) : '')}
                  freeSolo
                  onFocus={() => fetchAutocompleteOptions(name || '')}
                  autoHighlight
                  autoSelect
                  clearable
                  disabled={loading || disabled}
                  sx={{ minWidth: isMdDown ? '150px' : '190px' }}
                  onInputChange={(v) => {
                    fetchAutocompleteOptions(v);
                    setName(v);
                  }}
                  placeholder="Name (optional)"
                  options={name ? [{label: name}, ...participantAutocompleteOptions].map((participant, index) => {
                    return { value: participant.label, label: participant.label, key: index };
                  }) : participantAutocompleteOptions.map((participant, index) => {
                    return { value: participant.label, label: participant.label, key: index };
                  })
                  }
                />
              )}
            />
          </Grid>
          <Grid size={5}>
            {isAdditionalParticipantModal ? (
              <Typography
                sx={{
                  width: '%100%',
                  minWidth: isMdDown ? '150px' : '190px',
                  marginTop: 1
                }}>
                {email}
              </Typography>
            ) : (
              <Controller
                name="email"
                control={control}
                rules={{
                  required: true,
                  pattern: emailRegex,
                  validate: validateUniqueEmail,
                }}
                render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
                  <>
                    <CabAutocomplete<string, never>
                      {...field}
                      inputRef={ref}
                      onChange={(v) => handleChangeInput('email', v ? (Array.isArray(v) ? v[0] : v) : '')}
                      freeSolo
                      onFocus={() => fetchAutocompleteOptions(email || '')}
                      autoHighlight
                      autoSelect
                      clearable
                      disabled={loading || disabled}
                      sx={{ minWidth: isMdDown ? '150px' : '190px' }}
                      onInputChange={(v) => {
                        fetchAutocompleteOptions(v);
                        setEmail(v);
                      }}
                      placeholder="Email"
                      options={email ? [{label: email}, ...participantAutocompleteOptions].map((participant, index) => {
                        return { value: participant.label, label: participant.label, key: index };
                      }) : participantAutocompleteOptions.map((participant, index) => {
                        return { value: participant.label, label: participant.label, key: index };
                      })
                      }
                    />
                    <Typography color="error">
                      {error?.type === "required" ? "This field is required"
                        : error?.type === "pattern" ? "Please enter a valid email"
                          : error?.message || ''}
                    </Typography>
                  </>
                )}
              />
            )}
          </Grid>
          <Grid size={2}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 0.5
              }}>
              <CabButton
                type="submit"
                disabled={loading || disabled}
                sx={{ minWidth: 16, paddingTop: 1.2, paddingBottom: 1.2, paddingLeft: 1, paddingRight: 1, flex: 1 }}
              >
                {loading ? <CabSpinner scale={1} sx={{ width: 32 }} /> : submitButtonText}
              </CabButton>
              {onCancel && (
                <CabButton
                  disabled={loading || disabled}
                  sx={{ minWidth: 16, paddingTop: 1.2, paddingBottom: 1.2, paddingLeft: 1, paddingRight: 1, flex: 1 }}
                  onClick={onCancel}
                  buttonType="tertiary"
                >
                  <CabIcon Icon={IoCloseOutline} />
                </CabButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};


interface AttendeeListItemProps {
  attendee: Participant;
  onDeleteAttendee?: (attendeeId: number) => Promise<void> | void;
  onUpdateAttendee: (attendee: ParticipantUpdate) => Promise<void>;
  setEditingAttendee?: (attendeeId: number) => void;
  hideDeleteIcon?: boolean;
  isAdditionalParticipantModal?: boolean;
  hideCalendarAccessToggles?: boolean;
  meetingPreventConflicts?: boolean;
  loading?: boolean;
  meetingIsSaved: boolean;
  meetingIsPoll?: boolean;
  onInviteOrgLeader?: (leaderId: number) => Promise<void>;
  showOrgLeaderShareLink?: boolean;
  readOnly?: boolean;
  showAllowVotingToggle?: boolean;
}

const interactiveParticipantRowSx = {
  transition: 'background-color 150ms ease',
  '&:hover, &:focus-within': {
    backgroundColor: colors.black100,
  },
  '&:hover .participant-row-actions, &:focus-within .participant-row-actions': {
    opacity: 1,
  },
};

const participantRowSx = [
  {
    display: "flex",
    alignItems: "center",
    padding: 1,
    borderRadius: .75
  },
  ...(Array.isArray(interactiveParticipantRowSx) ? interactiveParticipantRowSx : [interactiveParticipantRowSx]),
];


const AttendeeListItem = ({
  attendee, onDeleteAttendee, onUpdateAttendee, setEditingAttendee, hideDeleteIcon, 
  isAdditionalParticipantModal, hideCalendarAccessToggles, meetingPreventConflicts, loading, meetingIsSaved,
  meetingIsPoll, onInviteOrgLeader, showOrgLeaderShareLink, readOnly, showAllowVotingToggle
}: AttendeeListItemProps): ReactElement => {
  const theme = useTheme();
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<null | HTMLElement>(null);
  const { confirm: confirmInvite, ConfirmationModalComponent: InviteConfirmationModal } = useConfirmationModalWithUI();
  const canExpand = !readOnly;
  const optionsOpen = Boolean(optionsAnchorEl);
  const displayName = attendee.name || attendee.email || '';
  const handleToggleProperty = async (
    property: 'required'|'should_invite'|'view_calendar'|'prevent_conflicts'|'allow_poll_voting'
  ) => {
    setPropertyLoading(true);
    try {
      await onUpdateAttendee({
        ...attendee,
        [property]: !attendee[property],
      });
    } finally {
      setPropertyLoading(false);
    }
  };

  const handleInviteOrgLeader = async () => {
    if (!attendee.orgLeaderId || !onInviteOrgLeader) return;
    const linkType = meetingIsPoll ? 'voting' : 'booking';
    const leaderLabel = attendee.name || attendee.email || 'this org leader';
    const notifyTarget = attendee.orgLeaderManagerName || `the user managing ${leaderLabel}`;
    const confirmed = await confirmInvite({
      title: `Send ${linkType} link?`,
      message: `This will notify ${notifyTarget} in Cabinet and include a link to the ${linkType} page.`,
      confirmText: `Send ${linkType} link`,
      cancelText: 'Cancel',
    });
    if (!confirmed) return;

    setInviteLoading(true);
    try {
      await onInviteOrgLeader(attendee.orgLeaderId);
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1
        }}>
        {/* Left Column: Avatar and name */}
        <Box
          sx={{
            display: 'flex',
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
            marginY: 0.5
          }}>
          {(
            attendee.iconSrc || attendee.color ? (
              <CabAvatar
                src={attendee.iconSrc}
                color={attendee.color}
                name={displayName}
                size="small"
                sx={{ marginLeft: 0.2 }}
              />
            ) : (
              <CabIcon
                Icon={IoPersonCircleOutline}
                color={colors.black500}
                sx={{ fontSize: 28, marginLeft: '-2px' }}
              />
            )
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flex: 1,
              minWidth: 0
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.2
              }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1
                }}>
                {displayName && (
                  <Typography
                    noWrap
                    sx={{
                      fontStyle: attendee.required ? 'unset' : 'italic',
                      fontSize: 15,
                      minWidth: 0
                    }}>
                    {displayName}
                  </Typography>
                )}
                {readOnly && attendee.isOrgLeader && showOrgLeaderShareLink && (
                  meetingIsPoll ? (
                    inviteLoading ? (
                      <CabSpinner scale={0.7} />
                    ) : (
                      <CabTooltip title="Send voting link" wrapWithSpan>
                        <IconButton
                          aria-label="Send voting link"
                          disabled={loading || propertyLoading}
                          onClick={handleInviteOrgLeader}
                          sx={{ padding: 0 }}
                        >
                          <MdHowToVote
                            color={theme.palette.mode === 'dark' ? colors.lavendar600 : colors.lavendar950}
                            size={24}
                          />
                        </IconButton>
                      </CabTooltip>
                    )
                  ) : (
                    <CabButton
                      size="small"
                      buttonType="primary"
                      disabled={loading || propertyLoading || inviteLoading}
                      onClick={handleInviteOrgLeader}
                      sx={{ height: 24, minHeight: 24, whiteSpace: 'nowrap', paddingX: 1.4, flexShrink: 0 }}
                    >
                      {inviteLoading ? <CabSpinner scale={0.7} /> : 'Send booking link'}
                    </CabButton>
                  )
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {meetingIsPoll && showAllowVotingToggle && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              marginY: 0.5
            }}>
            <CabTooltip
              title={attendee.allow_poll_voting ? 'Disable voting' : 'Allow voting'}
              wrapWithSpan
            >
              <IconButton
                aria-label={attendee.allow_poll_voting ? 'Disable voting' : 'Allow voting'}
                aria-pressed={!!attendee.allow_poll_voting}
                disabled={loading || propertyLoading}
                onClick={() => handleToggleProperty('allow_poll_voting')}
                sx={{ padding: 0 }}
              >
                <IoShareSocialSharp
                  color={attendee.allow_poll_voting
                    ? (theme.palette.mode === 'dark' ? colors.lavendar600 : colors.lavendar950)
                    : colors.black300}
                  size={24}
                />
              </IconButton>
            </CabTooltip>
          </Box>
        )}

        {canExpand && (
          <Box
            className="participant-row-actions"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              opacity: optionsOpen ? 1 : 0,
              transition: 'opacity 150ms ease',
              flexShrink: 0
            }}>
            <CabTooltip title={attendee.required ? 'Mark as optional' : 'Mark as required'} wrapWithSpan>
              <CabIcon
                size="small"
                Icon={attendee.required ? IoPerson : IoPersonOutline}
                alt={attendee.required ? 'Mark as optional' : 'Mark as required'}
                disabled={loading || propertyLoading}
                onClick={() => handleToggleProperty('required')}
              />
            </CabTooltip>
            {setEditingAttendee && (
              <CabTooltip title="Edit participant" wrapWithSpan>
                <CabIcon
                  size="small"
                  Icon={IoCreateOutline}
                  alt="Edit participant"
                  disabled={loading || propertyLoading}
                  onClick={() => setEditingAttendee(attendee.id)}
                />
              </CabTooltip>
            )}
            {!hideDeleteIcon && (
              <CabTooltip title="Delete participant" wrapWithSpan>
                <CabIcon
                  size="small"
                  Icon={IoTrashOutline}
                  alt="Delete participant"
                  disabled={loading || propertyLoading}
                  onClick={onDeleteAttendee ? () => onDeleteAttendee(attendee.id) : undefined}
                />
              </CabTooltip>
            )}
            {isAdditionalParticipantModal && (
              <CabTooltip title="Show more options" wrapWithSpan>
                <CabIcon
                  size="small"
                  Icon={IoEllipsisHorizontal}
                  alt="Show more options"
                  onClick={(event) => setOptionsAnchorEl(event.currentTarget)}
                />
              </CabTooltip>
            )}
          </Box>
        )}
      </Box>
      {canExpand && isAdditionalParticipantModal && (
        <Menu
          anchorEl={optionsAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={optionsOpen}
          onClose={() => setOptionsAnchorEl(null)}
          slotProps={{
            paper: { sx: { minWidth: 220 } },
            list: { 'aria-label': `More options for ${displayName}` }
          }}>
          <MenuItem dense>
            <CabControlLabel
              label='Invite to event'
              labelPlacement='end'
              labelGap={2}
              sx={{ height: 30 }}
              control={<CabSwitch
                checked={attendee.should_invite}
                disabled={loading || propertyLoading}
                onChange={() => handleToggleProperty('should_invite')}
                sx={{ transform: 'scale(0.8)' }}
              />}
            />
          </MenuItem>
          <MenuItem dense>
            <CabTooltip
              title={hideCalendarAccessToggles ? meetingIsSaved ? 'You don\'t have access to this calendar'
                : "You can use additional settings for participants once the meeting is saved."
                : !meetingPreventConflicts ? 'Prevent conflicts is turned off for this meeting' : ''}
              wrapWithSpan
            >
              <CabControlLabel
                label='Prevent conflicts'
                labelPlacement='end'
                labelGap={2}
                sx={{ height: 30 }}
                control={<CabSwitch
                  checked={attendee.prevent_conflicts}
                  disabled={hideCalendarAccessToggles || !meetingPreventConflicts || loading || propertyLoading}
                  onChange={() => handleToggleProperty('prevent_conflicts')}
                  sx={{ transform: 'scale(0.8)' }}
                />}
              />
            </CabTooltip>
          </MenuItem>
          <MenuItem dense>
            <CabTooltip
              title={hideCalendarAccessToggles ? meetingIsSaved ? 'You don\'t have access to this calendar'
                : "You can use additional settings for participants once the meeting is saved." : ''}
              wrapWithSpan
            >
              <CabControlLabel
                label='Show calendar'
                labelPlacement='end'
                labelGap={2}
                sx={{ height: 30 }}
                control={<CabSwitch
                  checked={attendee.view_calendar}
                  disabled={hideCalendarAccessToggles || loading || propertyLoading}
                  onChange={() => handleToggleProperty('view_calendar')}
                  sx={{ transform: 'scale(0.8)' }}
                />}
              />
            </CabTooltip>
          </MenuItem>
        </Menu>
      )}
      <InviteConfirmationModal />
    </>
  );
};


const AttendeeList = ({
  attendees, onDeleteAttendee, onAddAttendees, onUpdateAttendee, meetingLeaders, leaders, onRemoveLeader, onAddLeader, 
  onUpdateLeader, participantAutocompleteOptions, fetchAutocompleteOptions, isMdDown, hasAttendeeListOptions,
  isAdditionalParticipantModal, disableAdditionalParticipantsMessage, meetingPreventConflicts, meetingIsSaved,
  meetingIsPoll, orgLeaders, onHideAutocompleteContact, onInviteOrgLeader, showOrgLeaderShareLink,
  readOnlyPollShareList, onEditParticipants
}: AttendeeListProps): ReactElement => {
  const [addingAttendee, setAddingAttendee] = useState(false);
  const [editingAttendee, setEditingAttendee] = useState<number|null>(null);
  const [updatingAttendee, setUpdatingAttendee] = useState(false);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [addAutocompleteText, setAddAutocompleteText] = useState("");
  const [participantEmailPrompt, setParticipantEmailPrompt] = useState<{ name: string; email: string } | null>(null);
  const [participantEmailPromptError, setParticipantEmailPromptError] = useState("");
  const [bulkAddText, setBulkAddText] = useState("");
  const [parsedAttendees, setParsedAttendees] = useState<ParsedNameAndEmail[]>([]);
  const csvImportRef = createRef<HTMLInputElement>();

  const bottomRef = useRef<null | HTMLDivElement>(null);

  const orgLeadersByEmail = useMemo(() => {
    return new Map((orgLeaders || [])
      .filter(leader => leader.email)
      .map(leader => [leader.email.toLowerCase(), leader]));
  }, [orgLeaders]);

  const participantLeaders = meetingLeaders?.map(ml => {
    const leader = leaders?.find(l => l.id === ml.leader);
    const leaderInfoEmail = 'email' in ml && typeof ml.email === 'string' ? ml.email : '';
    const firstName = ml.first_name || leader?.first_name || '';
    const lastName = ml.last_name || leader?.last_name || '';

    return {
      ...ml,
      is_fetchable: ml.is_fetchable,
      meeting_leader_id: ml.id,
      leader_id: ml.leader,
      email: leader?.email || leaderInfoEmail,
      name: `${firstName} ${lastName}`.trim() || leader?.email || leaderInfoEmail,
      color: ml.color || leader?.color,
      iconSrc: getLeaderIconSrc(ml),
    };
  });

  const addParticipant = async (
    participant: Pick<PrivateExternalParticipantCreate, 'email' | 'name'>,
  ) => {
    setAddingAttendee(true);
    await onAddAttendees([{
      email: participant.email,
      name: participant.name,
      calendar_access: null,
      required: false,
      should_invite: true,
      timezone: getLocalTimeZoneNameOrNull()
    }]);
    setAddingAttendee(false);
  };

  const filterAddParticipantOptions = useMemo(
    () => createFilterOptions<AddParticipantAutocompleteOption>(),
    []
  );

  const addParticipantAutocompleteOptions = useMemo<AddParticipantAutocompleteOption[]>(() => {
    const meetingLeaderIds = new Set((meetingLeaders || []).map(ml => ml.leader));
    const meetingLeaderEmails = new Set((participantLeaders || []).map(leader => leader.email.toLowerCase()));
    const attendeeEmails = new Set(attendees.map(attendee => attendee.email?.toLowerCase()).filter(Boolean));
    const accessibleLeaderEmails = new Set((leaders || []).map(leader => leader.email.toLowerCase()));
    const search = addAutocompleteText.trim();

    const leaderOptions = (leaders || [])
      .filter(leader => !meetingLeaderIds.has(leader.id))
      .map(leader => ({
        value: `leader:${leader.id}`,
        label: `${leader.first_name} ${leader.last_name}`,
        meta: { group: 'Your leaders' },
        icon: <CabAvatar
          src={getLeaderIconSrc(leader)}
          color={leader.color}
          name={`${leader.first_name} ${leader.last_name}`}
          size="small"
        />
      }));

    const orgLeaderOptions = (orgLeaders || [])
      .filter(leader => leader.email)
      .filter(leader => !accessibleLeaderEmails.has(leader.email.toLowerCase()))
      .filter(leader => !attendeeEmails.has(leader.email.toLowerCase()))
      .map(leader => ({
        value: `orgLeader:${leader.id}`,
        label: `${leader.first_name} ${leader.last_name} (${leader.email})`,
        meta: { group: 'Org leaders' },
        icon: <CabAvatar
          src={getLeaderIconSrc(leader)}
          color={leader.color}
          name={`${leader.first_name} ${leader.last_name}`}
          size="small"
        />
      }));

    const contactOptions = participantAutocompleteOptions
      .filter(option => !attendeeEmails.has(option.email.toLowerCase()))
      .filter(option => !meetingLeaderEmails.has(option.email.toLowerCase()))
      .map(option => ({
        value: `contact:${option.email}`,
        label: option.label,
        meta: { group: 'Contacts', contactId: option.contactId },
      }));

    const customOption = search ? [{
      value: `custom:${search}`,
      label: `Add ${search}`,
      meta: { group: 'Add' },
    }] : [];

    return [...leaderOptions, ...orgLeaderOptions, ...contactOptions, ...customOption];
  }, [
    addAutocompleteText, attendees, leaders, meetingLeaders, orgLeaders, participantAutocompleteOptions,
    participantLeaders
  ]);

  const handleUnifiedAddChange = async (value: string | string[] | null) => {
    const selectedValue = Array.isArray(value) ? value[0] : value;
    if (!selectedValue) return;
    const [type, rawValue] = selectedValue.split(/:(.*)/s);
    const clearAddAutocomplete = () => {
      setAddAutocompleteText('');
      window.setTimeout(() => setAddAutocompleteText(''), 0);
    };

    if (type === 'leader' && onAddLeader) {
      onAddLeader(Number(rawValue));
      clearAddAutocomplete();
      return;
    }

    if (type === 'orgLeader') {
      const leader = (orgLeaders || []).find(item => item.id === Number(rawValue));
      if (leader?.email) {
        await addParticipant({
          email: leader.email,
          name: `${leader.first_name} ${leader.last_name}`,
        });
      }
      clearAddAutocomplete();
      return;
    }

    if (type === 'contact') {
      const contact = participantAutocompleteOptions.find(option => option.email === rawValue);
      await addParticipant({
        email: contact?.email || rawValue,
        name: contact?.name,
      });
      clearAddAutocomplete();
      return;
    }

    if (type === 'custom') {
      const parsed = parseEmailAddresses(rawValue)[0];
      if (!parsed || !emailRegex.test(parsed.email)) {
        setParticipantEmailPrompt({ name: rawValue.trim(), email: "" });
        setParticipantEmailPromptError("");
        return;
      }
      await addParticipant({
        email: parsed.email,
        name: parsed?.name || undefined,
      });
      clearAddAutocomplete();
    }
  };

  const handleAddAutocompleteKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;

    const firstOption = filterAddParticipantOptions(addParticipantAutocompleteOptions, {
      inputValue: addAutocompleteText,
      getOptionLabel: option => option.label,
    })[0];
    if (!firstOption) return;

    event.preventDefault();
    event.stopPropagation();
    void handleUnifiedAddChange(firstOption.value);
  };

  const closeParticipantEmailPrompt = () => {
    setParticipantEmailPrompt(null);
    setParticipantEmailPromptError("");
  };

  const addPromptedParticipant = async () => {
    if (!participantEmailPrompt) return;

    const email = participantEmailPrompt.email.trim();
    if (!emailRegex.test(email)) {
      setParticipantEmailPromptError("Please enter a valid email.");
      return;
    }

    await addParticipant({
      email,
      name: participantEmailPrompt.name.trim() || undefined,
    });
    closeParticipantEmailPrompt();
    setAddAutocompleteText('');
  };

  const categorizedAttendees = useMemo(() => {
    const leaderEmailSet = new Set((participantLeaders || []).map(leader => leader.email.toLowerCase()));
    return {
      orgLeaderAttendees: attendees.filter(attendee => {
        const email = attendee.email?.toLowerCase();
        return !!email && !leaderEmailSet.has(email) && orgLeadersByEmail.has(email);
      }),
      externalAttendees: attendees.filter(attendee => {
        const email = attendee.email?.toLowerCase();
        return !email || !orgLeadersByEmail.has(email) || leaderEmailSet.has(email);
      }),
    };
  }, [attendees, orgLeadersByEmail, participantLeaders]);

  const handleAddSubmit: SubmitHandler<FormInput> = async (data) => {
    setAddingAttendee(true);
    await onAddAttendees([{
      email: data.email,
      name: data.name,
      calendar_access: null,
      required: data.required,
      should_invite: data.inviteToMeeting,
      timezone: getLocalTimeZoneNameOrNull()
    }]);
    setAddingAttendee(false);
  };

  const handleEditSubmit = async (data: FormInput) => {
    if (data.id) {
      const attendee = attendees.find(a => a.id === data.id);
      setUpdatingAttendee(true);
      await onUpdateAttendee({
        id: data.id,
        email: data.email,
        required: attendee?.required || false,
        should_invite: data.inviteToMeeting,
        view_calendar: data.showCalendar,
        prevent_conflicts: data.preventConflicts,
        name: data.name,
        calendar_access: attendee?.calendar_access || null
      });
      setUpdatingAttendee(false);
    }
    setEditingAttendee(null);
  };

  const handleUpdateMeetingLeader = async (leader: ParticipantUpdate) => {
    if (!onUpdateLeader) return;

    const ldr = participantLeaders?.find(l => l.meeting_leader_id === leader.id);
    if (!ldr?.meeting_leader_id) return;
    setUpdatingAttendee(true);
    await onUpdateLeader(
      { ...leader, id: ldr.meeting_leader_id, meeting: ldr.meeting, leader: ldr.leader_id },
      ldr.leader_id,
    );
    setUpdatingAttendee(false);
  };

  const handleUpdateAttendee = async (attendee: ParticipantUpdate) => {
    setUpdatingAttendee(true);
    await onUpdateAttendee(attendee);
    setUpdatingAttendee(false);
  };

  const tryParseBulkAdd = (input: string) => {
    const namesAndEmails = parseEmailAddresses(input);
    const dedupedNamesAndEmails: ParsedNameAndEmail[] = [];
    const seenEmails: {[email: string]: number} = {};
    let idx = 0;
    namesAndEmails.forEach(entry => {
      const existingIdx = seenEmails[entry.email];
      if (existingIdx != undefined) {
        // Email has already been - overwrite name if not already present
        if (dedupedNamesAndEmails[existingIdx] && !dedupedNamesAndEmails[existingIdx].name) {
          dedupedNamesAndEmails[existingIdx].name = entry.name;
        }
      } else {
        dedupedNamesAndEmails.push(entry);
        seenEmails[entry.email] = idx;
        idx += 1;
      }
    });
    setParsedAttendees(namesAndEmails);
  };

  const closeBulkAddModal = () => {
    setBulkAddOpen(false);
    setBulkAddText("");
    setParsedAttendees([]);
  };

  const addParsedAttendees = async () => {
    setAddingAttendee(true);
    const existingAttendeeEmails = attendees.map(a => a.email?.toLocaleLowerCase());
    const attendeesToAdd: Omit<PrivateExternalParticipantCreate, 'meeting'>[] = [];
    parsedAttendees.forEach(attendee => {
      if (!existingAttendeeEmails.includes(attendee.email)) {
        attendeesToAdd.push({
          email: attendee.email,
          name: attendee.name || undefined,
          calendar_access: null,
          required: true,
          should_invite: true,
          timezone: getLocalTimeZoneNameOrNull()
        });
        existingAttendeeEmails.push(attendee.email);
      }
    });
    await onAddAttendees(attendeesToAdd);
    setAddingAttendee(false);
    closeBulkAddModal();
  };

  const handleImportCsvClick = (): void => {
    if (csvImportRef.current) {
      csvImportRef.current.click();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        gap: 1,
        flexDirection: 'column'
      }}>
      {isAdditionalParticipantModal && !readOnlyPollShareList && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
          <CabAutocomplete<string, AddParticipantAutocompleteMeta>
            value={null}
            inputValue={addAutocompleteText}
            options={addParticipantAutocompleteOptions}
            onChange={handleUnifiedAddChange}
            onInputChange={(value) => {
              setAddAutocompleteText(value);
              fetchAutocompleteOptions(value);
            }}
            onKeyDown={handleAddAutocompleteKeyDown}
            onFocus={() => fetchAutocompleteOptions(addAutocompleteText || '')}
            groupBy={option => option.meta?.group || ''}
            filterOptions={filterAddParticipantOptions}
            renderOption={(props, option) => {
              const key = option.key ? `${option.key}` : `${option.value}`;
              const {key: menuItemKey, ...menuItemProps} = props;
              const contact = option.meta?.contactId
                ? participantAutocompleteOptions.find(item => item.contactId === option.meta?.contactId)
                : undefined;
              const hideContactVisibleSelector = "&:hover .participant-autocomplete-hide-contact, "
                + "&:focus-within .participant-autocomplete-hide-contact";

              return (
                <Box
                  key={key}
                  component="li"
                  {...menuItemProps}
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    fontSize: "inherit",
                    gap: 1,
                    minHeight: 36,
                    "& .participant-autocomplete-hide-contact": {
                      opacity: 0,
                      transition: "opacity 150ms ease",
                    },
                    [hideContactVisibleSelector]: {
                      opacity: 1,
                    },
                  }}
                >
                  {option.icon}
                  <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {option.label}
                  </Box>
                  {contact && onHideAutocompleteContact && (
                    <Box
                      className="participant-autocomplete-hide-contact"
                      component="span"
                      sx={{ display: "flex", marginLeft: "auto", flexShrink: 0 }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      <CabTooltip title="Hide this contact from participant search" wrapWithSpan placement="left">
                        <CabIcon
                          size="small"
                          Icon={IoCloseOutline}
                          alt="Hide contact"
                          onClick={() => onHideAutocompleteContact(contact)}
                        />
                      </CabTooltip>
                    </Box>
                  )}
                </Box>
              );
            }}
            placeholder="Name or email"
            clearable
            autoHighlight
            loading={addingAttendee}
            ListboxProps={{
              sx: {
                '& .MuiMenuItem-root': {
                  gap: 1,
                },
              },
            }}
            sx={{ flex: 1 }}
          />
          <CabButton
            buttonType="tertiary"
            onClick={() => setBulkAddOpen(true)}
            icon={<CabIcon alt="Bulk add" Icon={IoListOutline} />}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Bulk Add
          </CabButton>
        </Box>
      )}
      <CabModal
        open={!!participantEmailPrompt}
        onClose={closeParticipantEmailPrompt}
        title="Add Participant"
        closeOnBackdropClick={!addingAttendee}
        noFullScreen
        actionButtons={
          <>
            <CabButton buttonType="secondary" disabled={addingAttendee} onClick={closeParticipantEmailPrompt}>
              Cancel
            </CabButton>
            <CabButton disabled={addingAttendee} onClick={addPromptedParticipant}>
              Add
            </CabButton>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 1 }}>
          <CabTextInput
            fullWidth
            label="Name"
            value={participantEmailPrompt?.name || ""}
            disabled={addingAttendee}
            onChange={(event) => setParticipantEmailPrompt(current => current
              ? { ...current, name: event.target.value }
              : current)}
          />
          <CabTextInput
            fullWidth
            required
            label="Email"
            type="email"
            value={participantEmailPrompt?.email || ""}
            disabled={addingAttendee}
            onChange={(event) => {
              setParticipantEmailPromptError("");
              setParticipantEmailPrompt(current => current
                ? { ...current, email: event.target.value }
                : current);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void addPromptedParticipant();
              }
            }}
            overrides={{
              error: !!participantEmailPromptError,
              helperText: participantEmailPromptError,
            }}
          />
        </Box>
      </CabModal>
      {isAdditionalParticipantModal && !readOnlyPollShareList && (
        <Box
          sx={{
            height: '100%',
            overflow: 'clip',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
          <Typography variant="h2" sx={{
            marginTop: 1
          }}> Teammates </Typography>
          <Box
            sx={{
              maxHeight: '80%',
              overflow: 'auto',
              direction: 'rtl',

              '::-webkit-scrollbar': {
                width: 4,
                height: 8,
                backgroundColor: 'none',
              },

              '::-webkit-scrollbar-thumb': {
                background: colors.black500,
                borderRadius: 2,
              }
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: "100%",
                gap: .75,
                overflowX: 'hidden',
                direction: 'ltr'
              }}>
              {(participantLeaders || []).slice().reverse().map((leader, idx) => (
                <Box
                  key={leader.meeting_leader_id}
                  sx={participantRowSx}>
                  <AttendeeListItem
                    attendee={{ ...leader, id: leader.meeting_leader_id }}
                    onDeleteAttendee={onRemoveLeader}
                    onUpdateAttendee={handleUpdateMeetingLeader}
                    hideDeleteIcon={(participantLeaders || []).length <= 1}
                    isAdditionalParticipantModal={isAdditionalParticipantModal}
                    hideCalendarAccessToggles={!leader.is_fetchable}
                    meetingPreventConflicts={meetingPreventConflicts}
                    meetingIsSaved={meetingIsSaved}
                    meetingIsPoll={meetingIsPoll}
                  />
                </Box>
              ))}
              <div ref={bottomRef} />
            </Box>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          height: '100%',
          overflow: 'clip',
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
        <Box
          sx={{
            marginBottom: isAdditionalParticipantModal ? 0 : 0.5,
            display: 'flex',
            alignItems: "center",
            justifyContent: "space-between"
          }}>
          <Typography variant="h2"> 
            {readOnlyPollShareList
              ? 'Participants'
              : isAdditionalParticipantModal ? 'Other Guests' : 'Attendee List'}
          </Typography>
          {readOnlyPollShareList && onEditParticipants ? (
            <CabButton
              buttonType="tertiary"
              onClick={onEditParticipants}
              icon={<CabIcon alt="Edit" Icon={IoCreateOutline} />}
              sx={{border: 0, paddingLeft: 0, paddingRight: 0, paddingTop: '2px', ":hover": {border: 0}}}
            >
              Edit Participants
            </CabButton>
          ) : !isAdditionalParticipantModal ? (
            <CabButton 
              buttonType="tertiary" 
              onClick={() => setBulkAddOpen(true)} 
              icon={<CabIcon alt="Edit" Icon={IoListOutline} />}
              sx={{border: 0, paddingLeft: 0, paddingRight: 0, paddingTop: '2px', ":hover": {border: 0}}}
            >
              Bulk Add 
            </CabButton>
          ) : null}
        </Box>
        
        {disableAdditionalParticipantsMessage && (
          <Typography variant="body2"> 
            {disableAdditionalParticipantsMessage}
          </Typography>
        )}
        {hasAttendeeListOptions && !isAdditionalParticipantModal && !readOnlyPollShareList && (
          <AddEditAttendeeForm
            defaultValues={{
              email: '', name: '', required: false, inviteToMeeting: true, showCalendar: false, preventConflicts: false
            }}
            onSubmit={handleAddSubmit}
            submitButtonText="Add"
            loading={addingAttendee}
            participantAutocompleteOptions={participantAutocompleteOptions}
            fetchAutocompleteOptions={fetchAutocompleteOptions}
            resetAfterSubmit
            isMdDown={isMdDown}
            disabled={!!disableAdditionalParticipantsMessage}
            allOtherAttendees={attendees}
          />
        )}

        <Box
          sx={{
            maxHeight: '80%',
            overflow: 'auto',
            direction: 'rtl',

            '::-webkit-scrollbar': {
              width: 4,
              height: 8,
              backgroundColor: 'none',
            },

            '::-webkit-scrollbar-thumb': {
              background: colors.black500,
              borderRadius: 2,
            }
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: isAdditionalParticipantModal ? 0 : 1,
              width: "100%",
              gap: .75,
              overflowX: 'hidden',
              direction: 'ltr'
            }}>
            {readOnlyPollShareList && (participantLeaders || []).map((leader) => (
              <Box
                key={leader.meeting_leader_id}
                sx={participantRowSx}>
                <AttendeeListItem
                  attendee={{ ...leader, id: leader.meeting_leader_id }}
                  onUpdateAttendee={handleUpdateMeetingLeader}
                  hideCalendarAccessToggles={!leader.is_fetchable}
                  meetingPreventConflicts={meetingPreventConflicts}
                  meetingIsSaved={meetingIsSaved}
                  meetingIsPoll={meetingIsPoll}
                  readOnly
                  showAllowVotingToggle
                />
              </Box>
            ))}
            {categorizedAttendees.orgLeaderAttendees.slice().reverse().map((attendee) => {
              const orgLeader = attendee.email ? orgLeadersByEmail.get(attendee.email.toLowerCase()) : undefined;
              return (
                <Box
                  key={attendee.id}
                  sx={participantRowSx}>
                  {editingAttendee === attendee.id ? (
                    <Box sx={{
                      marginLeft: 1
                    }}>
                      <AddEditAttendeeForm
                        defaultValues={{
                          id: attendee.id, email: attendee.email || "", name: attendee.name,
                          required: attendee.required, inviteToMeeting: attendee.should_invite,
                          showCalendar: attendee.view_calendar, preventConflicts: attendee.prevent_conflicts,
                        }}
                        onSubmit={handleEditSubmit}
                        onCancel={() => setEditingAttendee(null)}
                        submitButtonText={<CabIcon Icon={IoCheckmark} sx={{ fontSize: 20 }} />}
                        loading={updatingAttendee}
                        participantAutocompleteOptions={participantAutocompleteOptions}
                        fetchAutocompleteOptions={fetchAutocompleteOptions}
                        isMdDown={isMdDown}
                        isAdditionalParticipantModal={isAdditionalParticipantModal || false}
                        disabled={!!disableAdditionalParticipantsMessage}
                        allOtherAttendees={attendees.filter(a => a.id !== attendee.id)}
                      />
                    </Box>
                  ) : (
                    <AttendeeListItem
                      attendee={{
                        ...attendee,
                        isOrgLeader: true,
                        orgLeaderId: orgLeader?.id,
                        orgLeaderManagerName: getOrgUserName(orgLeader?.primary_assistant)
                          || getOrgUserName(orgLeader?.user_profile),
                        iconSrc: orgLeader ? getLeaderIconSrc(orgLeader) : undefined,
                        color: orgLeader?.color,
                        view_calendar: !!(attendee.view_calendar && attendee.calendar_access),
                        prevent_conflicts: !!(attendee.prevent_conflicts && attendee.calendar_access),
                      }}
                      onDeleteAttendee={onDeleteAttendee}
                      onUpdateAttendee={handleUpdateAttendee}
                      setEditingAttendee={setEditingAttendee}
                      isAdditionalParticipantModal={isAdditionalParticipantModal}
                      hideCalendarAccessToggles={!attendee.is_fetchable}
                      meetingPreventConflicts={meetingPreventConflicts}
                      loading={attendee.meeting > 0 && attendee.id < 0}
                      meetingIsSaved={meetingIsSaved}
                      meetingIsPoll={meetingIsPoll}
                      showOrgLeaderShareLink={showOrgLeaderShareLink}
                      readOnly={readOnlyPollShareList}
                      onInviteOrgLeader={onInviteOrgLeader}
                    />
                  )}
                </Box>
              );
            })}
            {categorizedAttendees.externalAttendees.slice().reverse().map((attendee) => (
              <Box
                key={attendee.id}
                sx={participantRowSx}>
                {editingAttendee === attendee.id ? (
                  <Box sx={{
                    marginLeft: 1
                  }}>
                    <AddEditAttendeeForm
                      defaultValues={{
                        id: attendee.id, email: attendee.email || "", name: attendee.name,
                        required: attendee.required, inviteToMeeting: attendee.should_invite,
                        showCalendar: attendee.view_calendar, preventConflicts: attendee.prevent_conflicts,
                      }}
                      onSubmit={handleEditSubmit}
                      onCancel={() => setEditingAttendee(null)}
                      submitButtonText={<CabIcon Icon={IoCheckmark} sx={{ fontSize: 20 }} />}
                      loading={updatingAttendee}
                      participantAutocompleteOptions={participantAutocompleteOptions}
                      fetchAutocompleteOptions={fetchAutocompleteOptions}
                      isMdDown={isMdDown}
                      isAdditionalParticipantModal={isAdditionalParticipantModal || false}
                      disabled={!!disableAdditionalParticipantsMessage}
                      allOtherAttendees={attendees.filter(a => a.id !== attendee.id)}
                    />
                  </Box>
                ) : (
                  <AttendeeListItem
                    attendee={{
                      ...attendee,
                      view_calendar: !!(attendee.view_calendar && attendee.calendar_access),
                      prevent_conflicts: !!(attendee.prevent_conflicts && attendee.calendar_access),
                    }}
                    onDeleteAttendee={onDeleteAttendee}
                    onUpdateAttendee={handleUpdateAttendee}
                    setEditingAttendee={setEditingAttendee}
                    isAdditionalParticipantModal={isAdditionalParticipantModal}
                    hideCalendarAccessToggles={!attendee.is_fetchable}
                    meetingPreventConflicts={meetingPreventConflicts}
                    loading={attendee.meeting > 0 && attendee.id < 0}
                    meetingIsSaved={meetingIsSaved}
                    meetingIsPoll={meetingIsPoll}
                    showOrgLeaderShareLink={showOrgLeaderShareLink}
                    readOnly={readOnlyPollShareList}
                  />
                )}
              </Box>
            ))}
            <div ref={bottomRef} />
          </Box>
        </Box>
      </Box>
      <CabModal
        open={bulkAddOpen && !readOnlyPollShareList}
        onClose={closeBulkAddModal}
        closeOnBackdropClick={!addingAttendee}
        actionButtons={<>
          <CabButton disabled={addingAttendee} buttonType="secondary" onClick={closeBulkAddModal}>
            Cancel
          </CabButton>
          <CabButton disabled={addingAttendee} onClick={handleImportCsvClick}>
            Import CSV
            <input
              hidden
              type="file"
              accept={".csv"}
              ref={csvImportRef}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  const file: File = e.target.files[0];
                  file.text().then(res => {
                    setBulkAddText(res);
                    tryParseBulkAdd(res);
                  });
                }
              }}
            />
          </CabButton>
          <CabButton disabled={parsedAttendees.length === 0 || addingAttendee} onClick={addParsedAttendees}>
            {addingAttendee ? "Adding..." : "Add People"}
          </CabButton>
        </>}
        title={"Add Meeting Attendees"}
        sx={{
          '& .MuiDialog-paper': {
            minWidth: "50%",
            overflowX: "hidden"
          }
        }}
      >
        {addingAttendee 
          ? <Box sx={{
            marginBottom: 3
          }}>
            <Typography variant="body1" sx={{
              marginBottom: 5
            }}>
              Adding attendees - this may take a moment. Please don't close this window.
            </Typography>
            <CabSpinner scale={3} />
          </Box>
          : <>
            <Typography variant="body1" sx={{
              marginBottom: 1
            }}>
              Import a CSV or paste a list of names and email addresses below.
              Separate each person with commas, semicolons, or new lines.
            </Typography>
            <CabTextInput 
              fullWidth 
              multiline 
              minRows={4} 
              maxRows={10}
              value={bulkAddText}
              onChange={(e) => {
                const newBulkAddText = e.target.value;
                setBulkAddText(newBulkAddText);
                tryParseBulkAdd(newBulkAddText);
              }}
            />
            {parsedAttendees.length > 0 && <>
              <Typography variant="h5" sx={{
                marginTop: 2
              }}>
                Preview Attendees
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedAttendees.map(entry => (
                      <TableRow key={entry.email}>
                        <TableCell>{entry.name ? entry.name : "-"}</TableCell>
                        <TableCell>{entry.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
            }
          </>
        }
      </CabModal>
    </Box>
  );
};

export default AttendeeList;
